import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from "react";
import type { ReactNode } from "react";

/* ── Types ─────────────────────────────────────────── */

type ToastType = "success" | "error" | "info" | "warning";

interface Toast {
  id: string;
  type: ToastType;
  message: string;
  duration: number;
  /** Set to true when the dismiss animation starts */
  exiting?: boolean;
}

interface ToastContextValue {
  toast: (type: ToastType, message: string, duration?: number) => void;
}

/* ── Defaults ──────────────────────────────────────── */

const DEFAULT_DURATIONS: Record<ToastType, number> = {
  success: 4000,
  info: 4000,
  error: 6000,
  warning: 6000,
};

const MAX_VISIBLE = 5;
const EXIT_ANIMATION_MS = 300;

/* ── Icons (inline SVG, no extra deps) ─────────────── */

const ICONS: Record<ToastType, ReactNode> = {
  success: (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M7 10l2 2 4-4"
        stroke="#10b981"
        strokeWidth="2"
        strokeLinecap="round"
        strokeLinejoin="round"
      />
      <circle cx="10" cy="10" r="8" stroke="#10b981" strokeWidth="1.5" />
    </svg>
  ),
  error: (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="10" cy="10" r="8" stroke="#ef4444" strokeWidth="1.5" />
      <path
        d="M10 6v5M10 13.5v.5"
        stroke="#ef4444"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  ),
  warning: (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
    >
      <path
        d="M10 3l8 14H2L10 3z"
        stroke="#f59e0b"
        strokeWidth="1.5"
        strokeLinejoin="round"
      />
      <path
        d="M10 8v3M10 13.5v.5"
        stroke="#f59e0b"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  ),
  info: (
    <svg
      width="20"
      height="20"
      viewBox="0 0 20 20"
      fill="none"
      aria-hidden="true"
    >
      <circle cx="10" cy="10" r="8" stroke="#0284c7" strokeWidth="1.5" />
      <path
        d="M10 9v5M10 6.5v.5"
        stroke="#0284c7"
        strokeWidth="2"
        strokeLinecap="round"
      />
    </svg>
  ),
};

/* ── Context ───────────────────────────────────────── */

const ToastContext = createContext<ToastContextValue | null>(null);

/* ── Provider ──────────────────────────────────────── */

let nextId = 0;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<Toast[]>([]);
  const timers = useRef<Map<string, ReturnType<typeof setTimeout>>>(new Map());

  /** Begin the exit animation, then remove after it finishes. */
  const dismiss = useCallback((id: string) => {
    // Clear any existing auto-dismiss timer
    const existing = timers.current.get(id);
    if (existing) {
      clearTimeout(existing);
      timers.current.delete(id);
    }

    // Mark as exiting (triggers CSS animation)
    setToasts((prev) =>
      prev.map((t) => (t.id === id ? { ...t, exiting: true } : t)),
    );

    // Remove from DOM after exit animation completes
    setTimeout(() => {
      setToasts((prev) => prev.filter((t) => t.id !== id));
    }, EXIT_ANIMATION_MS);
  }, []);

  /** Schedule auto-dismiss for a toast. */
  const scheduleDismiss = useCallback(
    (id: string, duration: number) => {
      const timer = setTimeout(() => {
        timers.current.delete(id);
        dismiss(id);
      }, duration);
      timers.current.set(id, timer);
    },
    [dismiss],
  );

  const toast = useCallback(
    (type: ToastType, message: string, duration?: number) => {
      const id = `toast-${++nextId}`;
      const ms = duration ?? DEFAULT_DURATIONS[type];

      setToasts((prev) => {
        const next = [...prev, { id, type, message, duration: ms }];
        // Enforce max visible: remove oldest (non-exiting) first
        while (
          next.filter((t) => !t.exiting).length > MAX_VISIBLE &&
          next.length > 0
        ) {
          const oldest = next.find((t) => !t.exiting);
          if (!oldest) break;
          // Immediately remove the oldest to make room
          const idx = next.indexOf(oldest);
          next.splice(idx, 1);
          const oldTimer = timers.current.get(oldest.id);
          if (oldTimer) {
            clearTimeout(oldTimer);
            timers.current.delete(oldest.id);
          }
        }
        return next;
      });

      scheduleDismiss(id, ms);
    },
    [scheduleDismiss],
  );

  // Clean up all timers on unmount
  useEffect(() => {
    const t = timers.current;
    return () => {
      t.forEach((timer) => clearTimeout(timer));
      t.clear();
    };
  }, []);

  return (
    <ToastContext.Provider value={{ toast }}>
      {children}
      <div
        className="toast-container"
        aria-live="polite"
        aria-relevant="additions"
        role="status"
      >
        {toasts.map((t) => (
          <div
            key={t.id}
            className={`toast toast--${t.type}${t.exiting ? " toast--exit" : ""}`}
            role="alert"
          >
            <span className="toast__icon">{ICONS[t.type]}</span>
            <span className="toast__message">{t.message}</span>
            <button
              type="button"
              className="toast__close"
              onClick={() => dismiss(t.id)}
              aria-label="Dismiss notification"
            >
              <svg
                width="16"
                height="16"
                viewBox="0 0 16 16"
                fill="none"
                aria-hidden="true"
              >
                <path
                  d="M4 4l8 8M12 4l-8 8"
                  stroke="currentColor"
                  strokeWidth="1.5"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

/* ── Hook ──────────────────────────────────────────── */

export function useToast(): ToastContextValue {
  const ctx = useContext(ToastContext);
  if (!ctx) {
    throw new Error("useToast must be used within a <ToastProvider>");
  }
  return ctx;
}
